// Google Play 결제 클라이언트(Android TWA 앱, docs/monetization.md §5): Digital Goods API로 상품 현지 가격을 읽고,
// PaymentRequest('https://play.google.com/billing')로 구매 시트를 연 뒤 purchaseToken을 서버(/api/iap/play/complete)에 넘겨 지급받는다.
// 지갑은 서버 응답으로만 바꾼다(로컬 저장 금지). 순수 판정 함수는 DOM 없이 시험한다(play.test.ts).
// 앱 재시작 복구: 서버 주문 id를 클라이언트가 다시 알 수 없는 경우(세션 밖)는 서버 RTDN·Cron(maintainPlay)이 토큰으로 지급한다.
// 이 파일은 같은 세션(sessionStorage)에 마지막 {orderId, sku, token?}만 두었다가 purchaseToken이 있으면 complete를 다시 시도한다(서버 멱등).
import { won } from './model';

export const PLAY_BILLING_URL = 'https://play.google.com/billing';
/** payment-pending(409)이면 이 간격(ms)으로 complete를 다시 부른다(웹 경로와 같은 1.5초 단위 증가) */
export const PLAY_PENDING_DELAYS: readonly number[] = [1500, 3000, 4500, 6000, 7500];
export const PLAY_PENDING_TEXT = '확인 중 — 지급은 자동으로 됩니다';
export const PLAY_CANCEL_TEXT = '결제를 취소했어요. 청구되지 않았어요.';
export const PLAY_TEST_BAND = '테스트 결제 — 실제 청구 없음(라이선스 테스터)';
const PENDING_KEY = 'dungeonhero.iap.playPending';

export interface PlayPrice { currency: string; value: string }
export interface PlayItemDetails { itemId: string; price: PlayPrice }
export interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<PlayItemDetails[]>;
  listPurchases?(): Promise<Array<{ itemId: string; purchaseToken: string }>>;
}
/** 순수 판정이 읽는 window 모양(시험은 대역을 넣는다) */
export interface PlayEnv {
  getDigitalGoodsService?: (provider: string) => Promise<DigitalGoodsService>;
  PaymentRequest?: unknown;
}

/** Play 경로가 가능한 환경인가(Digital Goods + PaymentRequest 둘 다 있을 때만. 실제 성공 여부는 probe가 확인) */
export function playApiPresent(env: PlayEnv | null | undefined): boolean {
  return !!env && typeof env.getDigitalGoodsService === 'function' && typeof env.PaymentRequest === 'function';
}

/** 가격 표시: Play가 준 현지 가격을 우선, 없거나 이상하면 카탈로그 원화 */
export function playPriceText(krw: number, d: PlayPrice | null | undefined): string {
  if (!d || !d.currency || !Number.isFinite(Number(d.value)) || d.value === '') return won(krw);
  const n = Number(d.value);
  try { return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: d.currency, maximumFractionDigits: d.currency === 'KRW' ? 0 : 2 }).format(n); }
  catch { return d.value + ' ' + d.currency; }
}

/** 사용자가 결제 시트를 닫았는가(PaymentRequest.show()는 취소 시 AbortError) */
export function isPlayCancel(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'AbortError';
}

/** complete 응답 코드 → 다음 행동: 다시 묻기(pending) / 끝(error) */
export function playCompleteAction(code: string): 'retry' | 'fail' {
  return code === 'payment-pending' || code === 'play-unavailable' || code === 'network' ? 'retry' : 'fail';
}

/**
 * PaymentRequest 구성(순수). 주의: Android Browser Helper billing 1.2.0의 MethodData는 obfuscatedAccountId를 읽지 않아 Play 구매에 계정 해시가 실리지 않는다.
 * 그래서 complete 전에 앱이 꺼진 구매는 RTDN으로 계정에 잇지 못하고, 앱 실행·상점 열기 때의 listPurchases() 복원(/api/iap/play/restore)이 되찾는다. 값은 브리지가 지원하게 되면 바로 쓰이도록 그대로 보낸다.
 */
export function playPaymentRequestArgs(sku: string, obfuscatedAccountId: string): { methods: PaymentMethodData[]; details: PaymentDetailsInit } {
  return {
    methods: [{ supportedMethods: PLAY_BILLING_URL, data: { sku, obfuscatedAccountId } }],
    details: { total: { label: 'Total', amount: { currency: 'KRW', value: '0' } } },
  };
}

export interface PlayPending { orderId: string; sku: string; token?: string }
export function parsePending(raw: string | null): PlayPending | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<PlayPending> | null;
    if (!o || typeof o.orderId !== 'string' || typeof o.sku !== 'string') return null;
    return typeof o.token === 'string' && o.token ? { orderId: o.orderId, sku: o.sku, token: o.token } : { orderId: o.orderId, sku: o.sku };
  } catch { return null; }
}
export function loadPending(): PlayPending | null {
  try { return parsePending(sessionStorage.getItem(PENDING_KEY)); } catch { return null; }
}
export function savePending(p: PlayPending | null): void {
  try { if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p)); else sessionStorage.removeItem(PENDING_KEY); } catch { /* 저장소 거부 — 서버 RTDN·Cron이 처리 */ }
}

/** 앱(Play 설치)에서 Digital Goods 서비스를 얻는다. 없거나 실패하면 null(= APP_PAY_NOTICE로 폴백) */
export async function connectPlay(w: PlayEnv | undefined = typeof window === 'undefined' ? undefined : (window as unknown as PlayEnv)): Promise<DigitalGoodsService | null> {
  if (!playApiPresent(w)) return null;
  try { return (await w!.getDigitalGoodsService!(PLAY_BILLING_URL)) ?? null; } catch { return null; }
}

export const PLAY_RESTORED_TEXT = '지난 구매를 받았어요';
export interface PlayOwned { itemId: string; purchaseToken: string }

/**
 * listPurchases() 결과 중 이번 페이지 세션에서 아직 복원을 시도하지 않은 구매만(같은 토큰은 한 번 — 목록 안 중복도 한 번).
 * 형식이 이상한 항목(문자열 아님·빈 값)은 버린다. 결과 순서는 입력 순서.
 */
export function restoreTargets(list: readonly Partial<PlayOwned>[] | null | undefined, done: ReadonlySet<string>): PlayOwned[] {
  const out: PlayOwned[] = [], seen = new Set<string>();
  for (const x of list ?? []) {
    if (!x || typeof x.itemId !== 'string' || typeof x.purchaseToken !== 'string' || !x.itemId || !x.purchaseToken) continue;
    if (done.has(x.purchaseToken) || seen.has(x.purchaseToken)) continue;
    seen.add(x.purchaseToken);
    out.push({ itemId: x.itemId, purchaseToken: x.purchaseToken });
  }
  return out;
}

/** 복원 응답의 지갑 합계가 늘었을 때만 "지난 구매를 받았어요"를 보인다(이미 지급된 구매를 다시 확인한 것이면 조용히) */
export function restoreGained(before: number | null | undefined, after: number | null | undefined): boolean {
  return typeof before === 'number' && typeof after === 'number' && after > before;
}

/** SKU별 현지 가격 한 번에(실패하면 빈 맵 → 카탈로그 원화) */
export async function loadPlayPrices(svc: DigitalGoodsService, skus: string[]): Promise<Map<string, PlayPrice>> {
  const m = new Map<string, PlayPrice>();
  if (!skus.length) return m;
  try { for (const d of await svc.getDetails(skus)) if (d?.itemId && d.price) m.set(d.itemId, d.price); } catch { /* 가격 못 읽음 */ }
  return m;
}

/** 구매 시트를 열고 purchaseToken을 받는다. 사용자가 닫으면 AbortError가 그대로 던져진다 */
export async function requestPlayPurchase(sku: string, obfuscatedAccountId: string): Promise<string> {
  const a = playPaymentRequestArgs(sku, obfuscatedAccountId);
  const req = new PaymentRequest(a.methods, a.details);
  const resp = await req.show();
  const token = (resp.details as { purchaseToken?: unknown } | null)?.purchaseToken;
  if (typeof token !== 'string' || !token) { await resp.complete('fail'); throw new Error('no-token'); }
  await resp.complete('success');
  return token;
}
